import { useState, useEffect } from 'react';
import { Tag, ChevronDown, X, UserCog, CheckCircle, AlertCircle, Wallet } from 'lucide-react';
import { createPortal } from 'react-dom';
import API_BASE_URL from '../../config';
import axios from 'axios';
import UPIPaymentModal from './UPIPaymentModal';


const Loader = () => createPortal(
    <div className="fixed inset-0 bg-black/60 z-[999] flex items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-lime-400"></div>
        <p className="text-white ml-4">Loading...</p>
    </div>,
    document.body
);

const Extendplan = ({ member, onClose, onAddMember }) => {
    const getTodayDate = () => {
        const d = new Date();
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    };
    const [isLoading, setIsLoading] = useState(false);
    const today = getTodayDate();
    const expiry = member.ExpiryDate || today;
    const [walletCoins, setWalletCoins] = useState(0); // e.g., from userInfo or API
    const [useCoins, setUseCoins] = useState(false);
    const maxCoinsToUse = 150;

    const [plans, setPlans] = useState([]);
    const [trainers, setTrainers] = useState([]);
    const userInfo = JSON.parse(localStorage.getItem('fitscan_user'));

    const [bannerMessage, setBannerMessage] = useState('');
    const [bannerType, setBannerType] = useState(''); // 'success' or 'error'
    const [showUPIModal, setShowUPIModal] = useState(false);

    // Consolidated form state
    const [formData, setFormData] = useState({
        planId: '',
        planName: '',
        duration: 0,
        baseAmount: '',
        discount: '0',
        finalAmount: '',
        trainer: member.trainer || '',
        startDate: new Date(expiry) > new Date(today)
            ? (() => {
                const nextDay = new Date(expiry);
                nextDay.setDate(nextDay.getDate() + 1);
                return nextDay.toISOString().slice(0, 10);
            })()
            : today,
        extendedExpiry: '',
        walletCoins: '',
    });

    const showBanner = (message, type) => {
        setBannerMessage(message);
        setBannerType(type);
        setTimeout(() => {
            setBannerMessage('');
            setBannerType('');
        }, type === 'success' ? 3000 : 5000);
    };

    const handlePaymentReceived = async () => {
        setIsLoading(true);
        try {
            const paymentData = {
                PaymentId: null,
                UserId: member.username,
                PlanId: String(formData.planName), // Note: PlanId field is the plan name in this database schema!
                Amount: parseFloat(formData.finalAmount),
                PaymentStatus: 'Paid',
                Gymcode: userInfo.gymcode,
                PaymentMethod: 'UPI',
                PaidOn: getTodayDate(),
                TransactionId: `ExtendOAReg-${formData.phone}-${Date.now()}`,
                Notes: 'Payment completed via UPI QR',
                UpdatedBy: userInfo?.username || '',
                StartDate: formData.startDate,
                ExpiryDate: formData.extendedExpiry,
                TrainerUsername: formData.trainer
            };

            await axios.post(`${API_BASE_URL}/api/payment/add`, paymentData, {
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${JSON.parse(localStorage.getItem('accessToken') || '{}').token || ''}`
                }
            });

            showBanner("Payment recorded successfully!", 'success');
            setTimeout(() => {
                setShowUPIModal(false);
                setIsLoading(false);
                onAddMember();
                onClose();
            }, 1500);
        } catch (error) {
            console.error("Payment recording failed:", error);
            showBanner("Payment recording failed. Please try again.", 'error');
            setIsLoading(false);
        }
    };

    const handlePayLater = () => {
        // Record as pending payment
        const pendingPaymentData = {
            PaymentId: null,
            UserId: member.username,
            PlanId: String(formData.planName),
            Amount: parseFloat(formData.finalAmount),
            PaymentStatus: 'Pending',
            Gymcode: userInfo.gymcode,
            PaymentMethod: 'UPI-Pending',
            PaidOn: getTodayDate(),
            TransactionId: `ExtendPENDING-${formData.phone}-${Date.now()}`,
            Notes: 'Payment pending via UPI QR',
            UpdatedBy: userInfo?.username || '',
            StartDate: formData.startDate,
            ExpiryDate: formData.extendedExpiry,
            TrainerUsername: formData.trainer
        };

        // Optionally save pending payment to backend
        axios.post(`${API_BASE_URL}/api/payment/add`, pendingPaymentData, {
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${JSON.parse(localStorage.getItem('accessToken') || '{}').token || ''}`
            }
        }).catch(console.error);

        showBanner("Payment marked as pending. User can pay later.", 'success');
        setTimeout(() => {
            setShowUPIModal(false);
            onAddMember();
            onClose();
        }, 2000);
    };

    useEffect(() => {
        const fetchPlans = async () => {
            const res = await fetch(`${API_BASE_URL}/api/plan/gym/${userInfo.gymcode}`,
                {
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${JSON.parse(localStorage.getItem('accessToken') || '{}').token || ''}`
                    },
                });
            const data = await res.json();
            setPlans(data);
        };
        fetchPlans();

        const fetchTrainers = async () => {
            const response = await fetch(`${API_BASE_URL}/api/gym/gettrainers`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${JSON.parse(localStorage.getItem('accessToken') || '{}').token || ''}`
                },
                body: JSON.stringify(userInfo.gymcode)
            });
            const data = await response.json();
            setTrainers(data);
        };
        fetchTrainers();
    }, [userInfo.gymcode]);

    useEffect(() => {
        const fetchWalletCoins = async () => {
            try {
                const response = await fetch(`${API_BASE_URL}/api/wallet/balance/${member.username}`,
                    {
                        headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${JSON.parse(localStorage.getItem('accessToken') || '{}').token || ''}`,
                        }
                    }
                );
                const data = await response.json();
                setWalletCoins(Number(data?.balance) || 0); // Adjust the property per your API
            } catch (error) {
                setWalletCoins(0); // Default if fetch fails
            }
        };
        fetchWalletCoins();
    }, [member.username]);

    const handleUseCoinsToggle = (e) => {
        const checked = e.target.checked;
        setUseCoins(checked);
        if (checked) {
            // Apply max usage limit either maxCoinsToUse or available walletCoins
            const coinsToUse = walletCoins >= maxCoinsToUse ? maxCoinsToUse : walletCoins;
            setFormData(prev => ({
                ...prev,
                walletCoins: coinsToUse.toString(),
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                walletCoins: '0',
            }));
        }
    };

    // Plan selection handler
    const handlePlanChange = (e) => {
        const planId = e.target.value;
        const plan = plans.find(p => String(p.id) === planId);
        setFormData(prev => ({
            ...prev,
            planId: planId,
            planName: plan ? plan.planName : '',
            duration: plan ? plan.duration : 0,
            baseAmount: plan ? plan.amount : '',
        }));
    };

    // Generic input handler
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    // Calculate final amount when base amount or discount changes
    useEffect(() => {
        let finalAmount = Number(formData.baseAmount) || 0;
        const discountAmount = Number(formData.discount) || 0;
        const walletCoinsToUse = useCoins ? (Number(formData.walletCoins) || 0) : 0;
        if (discountAmount > 0) {
            finalAmount -= discountAmount;
        }
        if (walletCoinsToUse >= 150) {
            finalAmount -= walletCoinsToUse;
        }
        setFormData(prev => ({
            ...prev,
            finalAmount: finalAmount < 0 ? 0 : finalAmount,
        }));
    }, [formData.baseAmount, formData.discount, formData.walletCoins, useCoins]);



    // Recalculate extended expiry when plan/startDate changes
    useEffect(() => {
        if (formData.duration && formData.startDate) {
            const start = new Date(formData.startDate);
            start.setDate(start.getDate() + Number(formData.duration));
            setFormData(prev => ({
                ...prev,
                extendedExpiry: start.toISOString().slice(0, 10)
            }));
        } else {
            setFormData(prev => ({ ...prev, extendedExpiry: '' }));
        }
    }, [formData.duration, formData.startDate]);

    // Submission handler
    const handleFormSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        try {
            const payload = {
                Username: member.username,
                Gymcode: userInfo.gymcode,
                Planname: formData.planName,
                StartDate: formData.startDate,
                NewExpiryDate: formData.extendedExpiry,
                TrainerUsername: formData.trainer,
            };
            const gymUserResponse = await fetch(`${API_BASE_URL}/api/gym/extendUser`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${JSON.parse(localStorage.getItem('accessToken') || '{}').token || ''}`
                },
                body: JSON.stringify(payload),
            });

            if (!gymUserResponse.ok) {
                setIsLoading(false);
                const error = await gymUserResponse.json();
                showBanner(`Plan extension failed: ${error?.message || 'Unknown error'}`, 'error');
                return;
            }

            setIsLoading(false);
            if (useCoins) {
                try {
                    const res = await axios.post(`${API_BASE_URL}/api/Referral/usereferralreward`, member.username, {
                        headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${JSON.parse(localStorage.getItem('accessToken') || '{}').token || ''}`
                        },
                    });
                    if (res.data == null) {
                        console.error("Error while redeeming referral bonus");
                        showBanner("Unable to redeem referral bonus. Please contact us.", 'error');
                        setIsLoading(false);
                        // Refund coins to finalAmount
                        setFormData(prev => ({
                            ...prev,
                            finalAmount: (parseFloat(prev.finalAmount) + parseFloat(prev.walletCoins || '0')).toString()
                        }));

                        setIsLoading(false);
                    }

                } catch (err) {
                    console.error("Error while recording referralbonus:", err);
                    showBanner("Unable to redeem referral bonus. Please contact us.", 'error');
                    setFormData(prev => ({
                        ...prev,
                        finalAmount: (parseFloat(prev.finalAmount) + parseFloat(prev.walletCoins || '0')).toString()
                    }));

                    setIsLoading(false);
                }
            }
            setShowUPIModal(true);
        } catch (err) {
            console.error("Extension error:", err);
            showBanner("An error occurred while extending the member.", 'error');
            setIsLoading(false);
        }
    };

    return (
        <>
            {isLoading && <Loader />}

            {bannerMessage && (
                <div className={`fixed top-4 right-4 z-[100] p-4 rounded-lg flex items-center gap-3 max-w-sm shadow-xl ${bannerType === 'success'
                    ? 'bg-lime-500/60 border border-lime-500/50 text-lime-100'
                    : 'bg-red-500/60 border border-red-500/50 text-red-100'
                    }`}>
                    {bannerType === 'success' ? (
                        <CheckCircle size={20} />
                    ) : (
                        <AlertCircle size={20} />
                    )}
                    <span className="text-sm">{bannerMessage}</span>
                </div>
            )}
            <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4">
                <div className="bg-[#181818] rounded-xl border border-[#2b2b2b] w-full max-w-md max-h-[calc(100dvh-2rem)] overflow-y-auto overscroll-contain">
                    <div className="sticky top-0 z-10 flex items-center justify-between gap-4 bg-[#181818] px-6 py-4 border-b border-[#2b2b2b]">
                        <h3 className="text-lg font-bold text-white">Extend Membership</h3>
                        <button type="button" onClick={onClose} className="text-gray-400 hover:text-white" aria-label="Close extend membership">
                            <X size={24} />
                        </button>
                    </div>
                    <div className="px-6 pt-4 pb-[calc(1.5rem+env(safe-area-inset-bottom))]">
                        <form onSubmit={handleFormSubmit} className="space-y-4">
                            <div>
                                <label className="block text-sm text-gray-400 mb-1">Joining Date</label>
                                <input
                                    type="date"
                                    value={member.joinDate ? member.joinDate.slice(0, 10) : ''}
                                    readOnly
                                    className="w-full px-4 py-2 bg-[#2a2a2a] border border-[#2b2b2b] rounded-lg text-gray-400"
                                />
                            </div>
                            <div>
                                <label className="block text-sm text-gray-400 mb-1">Current Expiry Date</label>
                                <input
                                    type="date"
                                    value={member.ExpiryDate ? member.ExpiryDate.slice(0, 10) : ''}
                                    readOnly
                                    className="w-full px-4 py-2 bg-[#2a2a2a] border border-[#2b2b2b] rounded-lg text-gray-400"
                                />
                            </div>
                            <div>
                                <label className="block text-sm text-gray-400 mb-1">Select Plan</label>
                                <select
                                    value={formData.planId}
                                    onChange={handlePlanChange}
                                    required
                                    className="w-full px-4 py-2 bg-[#222] border border-[#2b2b2b] rounded-lg"
                                >
                                    <option value="">--Select Plan--</option>
                                    {plans.map(plan => (
                                        <option key={plan.id} value={plan.id}>
                                            {plan.planName}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            {formData.baseAmount && (
                                <div className="space-y-1">
                                    <label className="text-sm text-gray-400">Discount</label>
                                    <div className="relative">
                                        <Tag size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                                        <input
                                            type="number"
                                            name="discount"
                                            value={formData.discount}
                                            onChange={handleInputChange}
                                            className="w-full pl-10 pr-4 py-2 bg-[#2a2a2a] border border-[#2b2b2b] rounded-lg"
                                        />
                                    </div>
                                </div>
                            )}

                            {(formData.baseAmount && walletCoins >= 150) && (

                                <div className="space-y-1">

                                    <label className="text-sm text-gray-400">Wallet Coins</label>
                                    <div className="relative">
                                        <Wallet size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                                        <input
                                            type="text"
                                            name="walletCoins"
                                            value={formData.walletCoins}
                                            readOnly
                                            className="w-full pl-10 pr-4 py-2 bg-[#2a2a2a] border border-[#2b2b2b] rounded-lg"
                                        />
                                    </div>

                                    <div className='flex items-center space-x-2 text-sm text-gray-300'>

                                        <input
                                            type="checkbox"
                                            id="useCoins"
                                            checked={useCoins}
                                            onChange={handleUseCoinsToggle}
                                            className="w-4 h-4"
                                        />
                                        <label htmlFor="useCoins">
                                            Use Wallet Coins ({walletCoins} available) - Max {maxCoinsToUse} coins (1 coin = 1 ₹)
                                        </label>
                                    </div>
                                </div>
                            )}


                            {formData.baseAmount && (
                                <div>
                                    <label className="block text-sm text-gray-400 mb-1">Amount</label>
                                    <input
                                        type="text"
                                        value={formData.finalAmount}
                                        readOnly
                                        className="w-full px-4 py-2 bg-[#2a2a2a] border border-[#2b2b2b] rounded-lg text-gray-400"
                                    />
                                </div>
                            )}

                            <div>
                                <label className="block text-sm text-gray-400 mb-1">Start Date</label>
                                <input
                                    type="date"
                                    value={formData.startDate}
                                    onChange={e => setFormData(prev => ({ ...prev, startDate: e.target.value }))}
                                    className="w-full px-4 py-2 bg-[#222] border border-[#2b2b2b] rounded-lg text-white"
                                />
                            </div>
                            <div>
                                <label className="block text-sm text-gray-400 mb-1">Extended Expiry Date</label>
                                <input
                                    type="date"
                                    value={formData.extendedExpiry}
                                    readOnly
                                    className="w-full px-4 py-2 bg-[#2a2a2a] border border-[#2b2b2b] rounded-lg text-gray-400"
                                />
                            </div>
                            <div className="space-y-1">
                                <label className="text-sm text-gray-400">Trainer</label>
                                <div className="relative">
                                    <UserCog size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                                    <select
                                        name="trainer"
                                        value={formData.trainer}
                                        onChange={handleInputChange}
                                        className="w-full pl-10 pr-4 py-2 bg-[#222] border border-[#2b2b2b] rounded-lg focus:outline-none focus:ring-2 focus:ring-lime-400 appearance-none"
                                    >
                                        <option value="">Select Trainer</option>
                                        {trainers.map((trainer) => (
                                            <option key={trainer.id} value={trainer.email}>
                                                {trainer.fullName}
                                            </option>
                                        ))}
                                    </select>
                                    <ChevronDown size={16} className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                                </div>
                            </div>
                            <div className="flex justify-end gap-3 pt-4">
                                <button
                                    type="submit"
                                    className="px-6 py-2 bg-lime-400 text-[#181f10] rounded-lg hover:bg-lime-300 font-medium"
                                >
                                    Save
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>

            {/* UPI Payment Modal */}
            <UPIPaymentModal
                isOpen={showUPIModal}
                onClose={() => setShowUPIModal(false)}
                onPaymentReceived={handlePaymentReceived}
                onPayLater={handlePayLater}
                amount={formData.finalAmount}
                message={`Please proceed with the payment.`}
                transactionId={`REG-${formData.phone}-${Date.now()}`}
            />
        </>
    );
};

export default Extendplan;
